/* What has to happen next — derived, not typed in.
   Every step names the one thing standing between the project and its next
   stage, and who owes it. The client portal shows the client's; the studio
   cockpit shows both. Pure logic, no React, so it can be reasoned about and
   tested on its own. */

import type { Project, StageKey } from "./types";

export interface NextStep {
  id: string;
  who: "client" | "studio";
  label: string;
  detail?: string;
  /** The stage this unblocks. */
  stage: StageKey;
  tone: "wait" | "warn";
  /** Commercial — fee, budget or invoices. Held back from anything that goes
   *  to a designer, who has no business seeing the numbers. */
  money?: boolean;
}

const STAGE_LABEL: Record<StageKey, string> = {
  engagement: "Engagement", brief: "Design Brief", concept: "Concept Design", developed: "Developed Design",
  presentation: "Design Presentation", documentation: "Documentation", construction: "Construction Monitoring", delivery: "Delivery",
};
export function stageLabel(k: StageKey) { return STAGE_LABEL[k]; }

export function nextSteps(p: Project): NextStep[] {
  const out: NextStep[] = [];
  const add = (s: NextStep) => out.push(s);

  const eng = p.engagement?.status;
  const brief = p.brief?.status;
  const spaces = p.brief?.spaces ?? [];
  const sections = p.brief?.sections ?? [];
  const filled = sections.filter((s) => s.body?.trim()).length;
  const refs = p.brief?.references ?? [];
  const mood = p.moodboard?.images ?? [];
  const pres = p.presentation?.spaces ?? [];

  /* ---- engagement: nothing really starts until this is signed ---- */
  if (!p.estimate) {
    add({ id: "fee", who: "studio", stage: "engagement", tone: "wait", money: true,
      label: "Build the fee proposal", detail: "Set the scope and disciplines so the engagement can quote a fee." });
  }
  if (eng !== "signed") {
    add({ id: "sign", who: "client", stage: "engagement", tone: "wait",
      label: "Sign the letter of engagement", detail: "This is what formally starts the work — the fee, stages and terms are all in it." });
  }

  /* ---- brief: the target everything downstream is designed to ---- */
  if (brief === "draft") {
    if (filled < sections.length) {
      add({ id: "brief-fill", who: "client", stage: "brief", tone: "wait",
        label: `Fill in your brief${sections.length ? ` — ${filled}/${sections.length} sections done` : ""}`,
        detail: "Tell us the objectives, priorities and constraints. There are no wrong answers." });
    } else {
      add({ id: "brief-send", who: "studio", stage: "brief", tone: "wait",
        label: "Send the brief for approval", detail: "It's filled in — issue it to the client to sign off." });
    }
  }
  if (brief === "sent") {
    add({ id: "brief-approve", who: "client", stage: "brief", tone: "wait",
      label: "Review and approve the brief", detail: "Design can't start against a brief that isn't agreed." });
  }
  if (brief === "changes") {
    add({ id: "brief-changes", who: "studio", stage: "brief", tone: "warn",
      label: "Address the client's requested changes", detail: p.brief?.clientNote || "The client asked for changes to the brief." });
  }
  if (!spaces.length) {
    add({ id: "spaces", who: "client", stage: "brief", tone: "wait",
      label: "Add the spaces you need", detail: "The accommodation schedule sizes the whole project — and the fee." });
  }
  // No survey on file — ask the client to provide one, since design needs it.
  if (p.surveyPlan === "no" && !p.surveyDoc) {
    add({ id: "survey-provide", who: "client", stage: "brief", tone: "warn",
      label: "Provide a survey or site plan", detail: "There's no survey on file — share one so we can design to the real site rather than measure it first." });
  } else if (p.surveyPlan !== "yes" && p.surveyPlan !== "no") {
    // "To confirm" (or an older project with no value) — confirm whether one exists.
    add({ id: "survey", who: "client", stage: "brief", tone: "wait",
      label: "Confirm whether a survey plan exists", detail: "Without one the site has to be measured before design can begin." });
  }
  if (!p.budget) {
    add({ id: "budget", who: "client", stage: "brief", tone: "wait", money: true,
      label: "Confirm the construction budget", detail: "It sets the fee and keeps the design honest against what you want to spend." });
  }

  /* ---- concept: needs a direction to design toward ---- */
  if (!mood.length && !refs.length) {
    add({ id: "mood", who: "client", stage: "concept", tone: "wait",
      label: "Share reference images", detail: "Anything you're drawn to — rooms, materials, light. It's the fastest way to a direction that lands." });
  }
  if (brief === "approved" && !(p.concepts ?? []).length && !pres.length) {
    add({ id: "concept", who: "studio", stage: "concept", tone: "wait",
      label: "Develop the concept", detail: "The brief is approved — the direction can be explored." });
  }

  /* ---- presentation: decisions ---- */
  const pending = pres.filter((s) => s.response === "pending").length;
  const revise = pres.filter((s) => s.response === "revise").length;
  if (pending) {
    add({ id: "review", who: "client", stage: "presentation", tone: "wait",
      label: `Review ${pending} design${pending > 1 ? "s" : ""}`, detail: "Approve each space, or tell us what to change." });
  }
  if (revise) {
    add({ id: "revise", who: "studio", stage: "presentation", tone: "warn",
      label: `Revise ${revise} space${revise > 1 ? "s" : ""}`, detail: "The client asked for changes." });
  }

  /* ---- documentation & delivery ---- */
  const ift = (p.deliverables ?? []).filter((d) => d.phase === "IFT");
  const issued = ift.filter((d) => d.status === "issued").length;
  if (!ift.length) {
    add({ id: "register", who: "studio", stage: "documentation", tone: "wait",
      label: "Generate the drawing register", detail: "Lay out every sheet the set needs before production starts." });
  } else if (issued < ift.length && pres.length && !pending && !revise) {
    add({ id: "ift", who: "studio", stage: "documentation", tone: "wait",
      label: `Issue the tender set — ${issued}/${ift.length} done`, detail: "The design is approved, so the technical set can be produced and issued." });
  }
  if (ift.length && issued === ift.length && !p.links?.bid) {
    add({ id: "tender", who: "studio", stage: "delivery", tone: "wait",
      label: "Send the set out to tender", detail: "The IFT set is issued — hand it to the Bid Manager to price." });
  }

  /* ---- money owed — due/overdue only; scheduled isn't owed yet ---- */
  const owed = (p.invoices ?? []).filter((i) => i.status === "due" || i.status === "overdue");
  if (eng === "signed" && owed.length) {
    add({ id: "pay", who: "client", stage: "delivery", tone: "warn", money: true,
      label: `Settle ${owed.length} outstanding invoice${owed.length > 1 ? "s" : ""}`, detail: "Work continues stage by stage as invoices are settled." });
  }

  return out;
}

/** Everything except the commercial items — safe to put in front of a designer. */
export const designSteps = (p: Project) => nextSteps(p).filter((s) => !s.money);

export const clientSteps = (p: Project) => nextSteps(p).filter((s) => s.who === "client");
export const studioSteps = (p: Project) => nextSteps(p).filter((s) => s.who === "studio");
